import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { averyTemplates } from "./averyTemplates";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

export default function LabelSheetPreview({
  averyCode,
  labels,
  showCutLines,
  setShowCutLines,
  showFontWarning,
  zoom,
  setZoom,
  onGenerate,
  loading,
}) {
  const template = averyTemplates[averyCode];
  const [drag, setDrag] = useState({
    x: 0,
    y: 0,
    dragging: false,
    startX: 0,
    startY: 0,
  });
  const containerRef = useRef();

  if (!template)
    return <Box sx={{ color: "error.main", p: 2 }}>Unknown Avery template</Box>;

  // Calculate label positions and dimensions
  const labelWidth = template.pitchX - 2; // fudge for cut lines
  const labelHeight = template.pitchY - 2;

  // Drag handlers
  const onMouseDown = (e) => {
    setDrag({
      ...drag,
      dragging: true,
      startX: e.clientX - drag.x,
      startY: e.clientY - drag.y,
    });
  };
  const onMouseMove = (e) => {
    if (!drag.dragging) return;
    setDrag({
      ...drag,
      x: e.clientX - drag.startX,
      y: e.clientY - drag.startY,
      dragging: true,
    });
  };
  const onMouseUp = () => setDrag({ ...drag, dragging: false });

  const fontSize = 10 * zoom;

  return (
    <Box>
      {/* Controls row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={showCutLines}
                onChange={(e) => setShowCutLines(e.target.checked)}
              />
            }
            label="Show cut lines"
          />
        </Box>
        <Box sx={{ width: 180 }}>
          <Slider
            value={zoom}
            min={0.5}
            max={2}
            step={0.01}
            onChange={(_, v) => setZoom(v)}
            aria-label="Zoom"
          />
        </Box>
        <Box sx={{ minWidth: 60 }}>Zoom: {(zoom * 100).toFixed(0)}%</Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ borderRadius: 2, fontWeight: 600, px: 4, boxShadow: 1, ml: 2 }}
          disabled={loading}
          onClick={onGenerate}
          startIcon={loading ? <CircularProgress size={22} color="inherit" /> : null}
        >
          {loading ? 'Generating…' : 'Next: Generate PDF'}
        </Button>
      </Box>
      {/* Preview sheet area */}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "auto",
          background: "#fff",
          border: "2px solid #ddd",
          position: "relative",
          mx: "auto",
          py: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: template.pageWidth,
            height: template.pageHeight,
            position: "relative",
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            transition: "transform .2s",
            background: "#fff",
            boxShadow: 2,
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {/* Render labels */}
          {Array.from({ length: template.rows * template.cols }).map((_, i) => {
            const row = Math.floor(i / template.cols);
            const col = i % template.cols;
            const left = template.marginLeft + col * template.pitchX;
            const top = template.marginTop + row * template.pitchY;
            const labelText = labels[i] || "";
            return (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: left * zoom + drag.x,
                  top: top * zoom + drag.y,
                  width: labelWidth * zoom,
                  height: labelHeight * zoom,
                  border: showCutLines
                    ? "1.5px dashed #90caf9"
                    : "1.5px dashed transparent",
                  bgcolor: "#fff",
                  color: "#222",
                  fontSize: fontSize,
                  p: 1,
                  overflow: "hidden",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  transition: "border .2s, box-shadow .2s",
                  boxShadow: undefined,
                }}
              >
                {labelText}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
